import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAnalysisTreeContext } from "./AnalysisTreeContext";
import { CommentaryConfig } from "../containers/Interfaces";
import { StatefulRequestBuilder } from "../containers/stateful/StatefulRequests";
import { statefulAnalysisTree } from "../services/AnalysisTreeServices";
import { CommentaryProto, CommentaryProto_CommentaryConfig, InsightsProto } from "build-ts/analysis";
import { AnalysisTreeRequestType, StatefulResponse, StateKey } from "build-ts/stateful_api";
import { ChatMessage_Author } from "build-ts/chat";
import { runTigaChat } from "services/TigaServices";
import { AgentType, ChatRequest, TIGAInput } from "build-ts/agent";

interface ContextType {
  commentaryArray: CommentaryConfig[];
  setCommentaryArray: React.Dispatch<React.SetStateAction<CommentaryConfig[]>>;
  fetchCommentary: () => Promise<void>;
  loadingCommentary: boolean;
  hasAnyLoading: boolean;
  setHasAnyLoading: React.Dispatch<React.SetStateAction<boolean>>;
  updateCommentaryAtIndex: (
    index: number,
    newCommentary: CommentaryProto,
    isSaved: boolean
  ) => void;
  handleSelectCommentary: (commentary: CommentaryProto, index: number)=> void;
  selectedCommentaryIndex: number;
  transactionId: string;
  insights: CommentaryProto[];
  setInsights: React.Dispatch<React.SetStateAction<CommentaryProto[]>>;
  newInsights: InsightsProto[];
  setNewInsights: React.Dispatch<React.SetStateAction<InsightsProto[]>>;
}

const ApiContext = createContext<ContextType | undefined>(undefined);

export const useCommentaryContext = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApiContext must be used within an ApiProvider");
  }
  return context;
};

// Helper function to generate answer using TIGA chat.
export const generateTigaAnswer = async (question: string, transactionId: string): Promise<string> => {
  const tigaInput = TIGAInput.create({
    tree_session: StateKey.create({
      transaction_id: transactionId,
    }),
  });

  const chatRequest = ChatRequest.create({
    user_messages: [question],
    tiga_input: tigaInput,
    agent_type: AgentType.TIGA,
    client_tag: "FRIDA"
  });

  const response = await runTigaChat(chatRequest);

  // Extract the answer from the last AI message in the response
  let generatedAnswer = "";
  if (response[0]?.messages && response[0].messages.length > 0) {
    for (let i = response[0].messages.length - 1; i >= 0; i--) {
      if (response[0].messages[i].author === ChatMessage_Author.AI) {
        generatedAnswer = response[0].messages[i].content || "";
        break;
      }
    }
  }

  return generatedAnswer;
};

export const CommentaryContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  
  const { 
    tree,
    setSelectedCommentary, 
    generateCommentaryConfigJSON, 
    transactionId,
    setCommentaryNodes
   } = useAnalysisTreeContext();

  const [commentaryArray, setCommentaryArray] = useState<CommentaryConfig[]>([]);
  const [loadingCommentary, setLoadingCommentary] = useState<boolean>(false);
  const [hasAnyLoading, setHasAnyLoading] = useState<boolean>(false);
  const [selectedCommentaryIndex, setSelectedCommentaryIndex] = useState<number>(-1);
  const [insights, setInsights] = useState<CommentaryProto[]>([]);
  // New insights (Pair.of(question, answer)).
  const [newInsights, setNewInsights] = useState<InsightsProto[]>([]);

  //Generate commentary
  const fetchCommentary = async () => {

    const commentaryConfig: CommentaryProto_CommentaryConfig | null = generateCommentaryConfigJSON();
    const currentIndex = commentaryArray.length;
    try {
      setLoadingCommentary(true);
      setCommentaryArray((prev) => [
        ...prev,
        { index: currentIndex, loading: true, isSaved: false }, // Initialize isSaved here
      ]);

      const requestBuilder = new StatefulRequestBuilder(transactionId);
      const commentaryRequest = requestBuilder.GenerateCommentaryRequest(commentaryConfig!);
      const response: StatefulResponse = await statefulAnalysisTree(commentaryRequest);

      if(!response){
        // remove particular index from the commentary array if response is not ok.
        setCommentaryArray((prev) =>
          prev.filter((item, idx) => idx !== currentIndex)
        );
        return;
      } 
      const commentary = response.analysis_tree_responses
        ?.find((response) => response.type === AnalysisTreeRequestType.GENERATE_COMMENTARY)
        ?.generate_commentary?.commentary;
      
      setCommentaryArray((prev) =>
        prev.map((item, idx) =>
          idx === currentIndex
            ? { ...item, loading: false, commentary: commentary! }
            : item
        )
      );
      setLoadingCommentary(false);
      setSelectedCommentaryIndex(currentIndex);
      setSelectedCommentary(commentary!);
    } catch (error) {
      console.log("error");
    }
  };

  // Function to update commentary at a specific index
  const updateCommentaryAtIndex = (
    index: number,
    newCommentary: CommentaryProto,
    isSaved: boolean
  ) => {
    setCommentaryArray((prevCommentaryArray) => {
      const updatedArray = [...prevCommentaryArray];
      updatedArray[index] = {
        ...updatedArray[index],
        commentary: newCommentary,
        isSaved: isSaved,
      };
      return updatedArray;
    });
  };

  //selects the clicked commentary
  const handleSelectCommentary = (commentary: CommentaryProto, index: number) => {
    if (selectedCommentaryIndex === index) {
      // If the same commentary is clicked, unselect it
      setSelectedCommentary(null);
      setSelectedCommentaryIndex(-1);
      setCommentaryNodes(new Set());
    } else {
      // If a different commentary is clicked, select it
      setSelectedCommentary(commentary);
      setSelectedCommentaryIndex(index);
    }
  };

  //triggers when tree is fetched from URL treeId
  useEffect(() => {
    if (tree && tree.commentaries) {
      const initializedCommentaries: CommentaryConfig[] = tree.commentaries.map(
        (commentary: CommentaryProto, index: number) => ({
          index: index,
          loading: false,
          commentary: commentary,
          isSaved: true,
        })
      );
      setCommentaryArray(initializedCommentaries);

      setInsights(
        tree.commentaries.map((commentary) => {
          const questions = commentary?.questions?.map((q) => q || "");
          const answers = commentary?.answers?.map((a) => a || "");
          
          // If there are more questions than answers, push answers with empty string.
          while (answers.length < questions.length) {
            answers.push("");
          }
      
          return {
            ...commentary,
            questions,
            answers,
          };
        })
      );
    }
    if(tree?.insights){
      setNewInsights(tree?.insights);
    }
  }, [tree]);

  return (
    <ApiContext.Provider
      value={{
        commentaryArray,
        fetchCommentary,
        loadingCommentary,
        hasAnyLoading,
        setHasAnyLoading,
        setCommentaryArray,
        updateCommentaryAtIndex,
        handleSelectCommentary,
        selectedCommentaryIndex,
        transactionId,
        insights,
        setInsights,
        newInsights,
        setNewInsights
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};
